import React from "react";
import {
  Building2,
  Image as ImageIcon,
  Images,
  LayoutTemplate,
  User,
} from "lucide-react";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { ScrollArea } from "../ui/scroll-area";
import { cn } from "../../lib/utils";
import {
  PROJECT_IMAGE_MAX_UPLOAD_BYTES,
  projectImageOversizeMessage,
} from "../../lib/projectImageUploadLimits";

export type PhotoAssignmentSection =
  | "hero"
  | "about"
  | "gallery"
  | "profile"
  | "logo";

export type AssignmentProjectImage = {
  id: number;
  title: string;
  thumbUrl: string;
  aboutSection?: boolean;
  aboutSectionPosition?: number | null;
  showInProjectGallery?: boolean;
};

type ImageAssignmentModalProps = {
  open: boolean;
  onClose: () => void;
  initialSection: PhotoAssignmentSection;
  projectImages: AssignmentProjectImage[];
  heroProjectImageId: number | null | undefined;
  profileProjectImageId: number | null | undefined;
  logoProjectImageId: number | null | undefined;
  maxAboutPhotos: number;
  /** Max photos shown in the public portfolio grid (site gallery). */
  maxGalleryPhotos?: number;
  csrfToken: string;
  assignUrl: string;
  onSaved: (payload: {
    projectImages: AssignmentProjectImage[];
    heroBannerUrl?: string | null;
    heroProjectImageId?: number | null;
    logoUrl?: string | null;
    logoProjectImageId?: number | null;
    avatarUrl?: string | null;
    profileProjectImageId?: number | null;
  }) => void;
  uploadUrl?: string;
  canAddMoreImages?: boolean;
  onImagesUploaded?: (images: AssignmentProjectImage[]) => void;
};

const SECTIONS: {
  id: PhotoAssignmentSection;
  label: string;
  icon: React.ElementType;
}[] = [
  { id: "hero", label: "Homepage banner", icon: LayoutTemplate },
  { id: "about", label: "About section", icon: Images },
  { id: "gallery", label: "Portfolio on site", icon: ImageIcon },
  { id: "profile", label: "Profile photo", icon: User },
  { id: "logo", label: "Business logo", icon: Building2 },
];

export const ImageAssignmentModal: React.FC<ImageAssignmentModalProps> = ({
  open,
  onClose,
  initialSection,
  projectImages,
  heroProjectImageId,
  profileProjectImageId,
  logoProjectImageId,
  maxAboutPhotos,
  maxGalleryPhotos = 6,
  csrfToken,
  assignUrl,
  onSaved,
  uploadUrl,
  canAddMoreImages = true,
  onImagesUploaded,
}) => {
  const [section, setSection] =
    React.useState<PhotoAssignmentSection>(initialSection);
  const [draftHeroId, setDraftHeroId] = React.useState<number | null>(null);
  const [draftAboutIds, setDraftAboutIds] = React.useState<number[]>([]);
  const [draftGalleryIds, setDraftGalleryIds] = React.useState<number[]>([]);
  const [draftProfileId, setDraftProfileId] = React.useState<number | null>(
    null,
  );
  const [draftLogoId, setDraftLogoId] = React.useState<number | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (!open) return;
    setSection(initialSection);
    setError(null);
    setDraftHeroId(
      heroProjectImageId != null && Number.isFinite(Number(heroProjectImageId))
        ? Number(heroProjectImageId)
        : null,
    );
    const orderedAbout = projectImages
      .filter((img) => img.aboutSection)
      .slice()
      .sort(
        (a, b) => (a.aboutSectionPosition ?? 0) - (b.aboutSectionPosition ?? 0),
      )
      .map((img) => img.id);
    setDraftAboutIds(orderedAbout);
    const galleryVisible = projectImages
      .filter((img) => img.showInProjectGallery === true)
      .map((img) => img.id);
    setDraftGalleryIds(galleryVisible);
    setDraftProfileId(
      profileProjectImageId != null &&
        Number.isFinite(Number(profileProjectImageId))
        ? Number(profileProjectImageId)
        : null,
    );
    setDraftLogoId(
      logoProjectImageId != null && Number.isFinite(Number(logoProjectImageId))
        ? Number(logoProjectImageId)
        : null,
    );
    // Snapshot placements when the dialog opens. Do not reset drafts when
    // projectImages grows from an in-modal upload.
  }, [
    open,
    initialSection,
    heroProjectImageId,
    profileProjectImageId,
    logoProjectImageId,
  ]);

  const toggleAbout = (id: number) => {
    setDraftAboutIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= maxAboutPhotos) return prev;
      return [...prev, id];
    });
  };

  const toggleGallery = (id: number) => {
    setDraftGalleryIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= maxGalleryPhotos) return prev;
      return [...prev, id];
    });
  };

  const handleUploadFiles = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = event.target.files;
    if (!files || files.length === 0 || !uploadUrl) {
      event.target.value = "";
      return;
    }
    const fileArray = Array.from(files);
    const oversized = fileArray.find(
      (file) => file.size > PROJECT_IMAGE_MAX_UPLOAD_BYTES,
    );
    if (oversized) {
      setError(projectImageOversizeMessage(oversized.name));
      event.target.value = "";
      return;
    }

    setUploading(true);
    setError(null);
    const uploaded: AssignmentProjectImage[] = [];
    try {
      for (const file of fileArray) {
        const title =
          file.name.replace(/\.[^/.]+$/, "").replace(/[-_]/g, " ") ||
          "Project photo";
        const formData = new FormData();
        formData.append("image", file);
        formData.append("title", title);
        const response = await fetch(uploadUrl, {
          method: "POST",
          headers: { "X-CSRF-Token": csrfToken },
          body: formData,
        });
        const data = await response.json();
        if (!response.ok) {
          setError(
            typeof data.error === "string" && data.error.trim()
              ? data.error
              : "Upload failed.",
          );
          continue;
        }
        if (data.project_image) uploaded.push(data.project_image);
      }
      if (uploaded.length > 0) onImagesUploaded?.(uploaded);
    } catch {
      setError("Could not upload. Please try again.");
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  };

  const showAddPhotos = Boolean(uploadUrl) && canAddMoreImages;

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const response = await fetch(assignUrl, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({
          hero_project_image_id: draftHeroId,
          about_section_image_ids: draftAboutIds,
          project_gallery_image_ids: draftGalleryIds,
          profile_project_image_id: draftProfileId,
          logo_project_image_id: draftLogoId,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        setError(data.error || "Could not save.");
        return;
      }
      onSaved({
        projectImages: data.project_images || [],
        heroBannerUrl: data.heroBannerUrl,
        heroProjectImageId: data.heroProjectImageId,
        logoUrl: data.logoUrl,
        logoProjectImageId: data.logoProjectImageId,
        avatarUrl: data.avatarUrl,
        profileProjectImageId: data.profileProjectImageId,
      });
      onClose();
    } catch {
      setError("Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const empty = projectImages.length === 0;

  const sectionHint: Record<PhotoAssignmentSection, string> = {
    hero: "Pick one image for the large top area of your public homepage. Clear to use the default hero.",
    about: `Choose up to ${maxAboutPhotos} photos for About Us (${draftAboutIds.length}/${maxAboutPhotos} selected).`,
    gallery: `Pick up to ${maxGalleryPhotos} photos for your public portfolio (${draftGalleryIds.length}/${maxGalleryPhotos}). New uploads stay off the portfolio until you add them here. The same photo can be used in more than one place.`,
    profile:
      "Pick the face photo shown with your business on the public site. Clear to use your Google sign-in photo when available.",
    logo: "Pick a square or wide mark for the header, footer, and browser tab. Clear to hide the logo.",
  };

  const showClearRow =
    section === "hero" || section === "profile" || section === "logo";

  const gridCols =
    "grid gap-2 grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-4 xl:grid-cols-5";

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
    >
      <DialogContent
        aria-describedby="image-assignment-desc"
        aria-labelledby="image-assignment-title"
        data-image-assignment-dialog
        className={cn(
          "w-[min(100vw-1.5rem,60rem)] max-w-none gap-0 p-0 sm:w-[min(100vw-2rem,60rem)]",
          "flex flex-col overflow-hidden rounded-2xl",
          // Below lg: full-screen sheet using dvh (mobile Safari toolbar-safe); shadcn defaults center
          // with translate and intrinsic height, which reads as a short floating card on iOS.
          "max-lg:inset-0 max-lg:h-dvh max-lg:max-h-dvh max-lg:w-full max-lg:max-w-none max-lg:translate-x-0 max-lg:translate-y-0 max-lg:rounded-none",
        )}
      >
        <DialogTitle id="image-assignment-title" className="sr-only">
          Photo library and placements
        </DialogTitle>
        <DialogHeader className="shrink-0 space-y-0 border-b border-border px-5 py-4 pr-12 text-left">
          <div className="min-w-0 space-y-1">
            <p
              className="text-lg font-semibold leading-none tracking-tight"
              aria-hidden="true"
            >
              Photo library and placements
            </p>
            <DialogDescription
              id="image-assignment-desc"
              className="text-xs sm:text-sm"
            >
              Choose which images appear on your website in every section
            </DialogDescription>
          </div>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden lg:flex-row">
          <aside className="flex shrink-0 flex-col border-b border-border lg:w-52 lg:border-b-0 lg:border-r">
            <ScrollArea
              className="max-h-36 w-full lg:max-h-none"
              dir="ltr"
              type="scroll"
            >
              <nav
                className="flex gap-1 p-2 lg:flex-col lg:gap-0.5"
                aria-label="Placement categories"
              >
                {SECTIONS.map(({ id, label, icon: Icon }) => {
                  const active = section === id;
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setSection(id)}
                      className={cn(
                        "flex items-center gap-2 rounded-lg px-3 py-2.5 text-left text-sm font-medium transition-colors",
                        active
                          ? "bg-premium-500/10 text-premium-700"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground",
                      )}
                    >
                      <Icon className="h-4 w-4 shrink-0 opacity-80" />
                      <span className="leading-tight">{label}</span>
                    </button>
                  );
                })}
              </nav>
            </ScrollArea>
          </aside>

          <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
            <div className="shrink-0 space-y-2 border-b border-border px-5 py-3">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <p className="text-sm text-muted-foreground">
                  {empty
                    ? "Add photos here, then pick where they appear on the site."
                    : sectionHint[section]}
                </p>
                {showAddPhotos && !empty ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={uploading}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {uploading ? "Uploading…" : "Add photos"}
                  </Button>
                ) : null}
              </div>
            </div>

            <div
              className={cn(
                "flex min-h-11 shrink-0 items-center border-b border-border px-5 py-2",
                !showClearRow && "min-h-11",
              )}
            >
              {section === "hero" && draftHeroId != null ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setDraftHeroId(null)}
                >
                  Clear homepage banner
                </Button>
              ) : null}
              {section === "profile" && draftProfileId != null ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setDraftProfileId(null)}
                >
                  Clear profile photo
                </Button>
              ) : null}
              {section === "logo" && draftLogoId != null ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setDraftLogoId(null)}
                >
                  Clear business logo
                </Button>
              ) : null}
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4 sm:p-5">
              {empty ? (
                <div className="flex min-h-40 flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-border px-6 py-10 text-center">
                  <p className="text-sm text-muted-foreground">
                    No photos yet. Add one to use as a profile photo, logo, or
                    site image.
                  </p>
                  {showAddPhotos ? (
                    <Button
                      type="button"
                      variant="premium"
                      size="sm"
                      disabled={uploading}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      {uploading ? "Uploading…" : "Add photos"}
                    </Button>
                  ) : null}
                </div>
              ) : (
                <div className={gridCols}>
                  {projectImages.map((img) => {
                    const inAbout = draftAboutIds.includes(img.id);
                    const inGallery = draftGalleryIds.includes(img.id);
                    const isHero = draftHeroId === img.id;
                    const isProfile = draftProfileId === img.id;
                    const isLogo = draftLogoId === img.id;

                    let ringActive = false;
                    if (section === "hero") ringActive = isHero;
                    else if (section === "about") ringActive = inAbout;
                    else if (section === "gallery") ringActive = inGallery;
                    else if (section === "profile") ringActive = isProfile;
                    else if (section === "logo") ringActive = isLogo;

                    const handlePick = () => {
                      if (section === "hero") {
                        setDraftHeroId(isHero ? null : img.id);
                      } else if (section === "about") {
                        toggleAbout(img.id);
                      } else if (section === "gallery") {
                        toggleGallery(img.id);
                      } else if (section === "profile") {
                        setDraftProfileId(isProfile ? null : img.id);
                      } else if (section === "logo") {
                        setDraftLogoId(isLogo ? null : img.id);
                      }
                    };

                    const disabledAbout =
                      section === "about" &&
                      !inAbout &&
                      draftAboutIds.length >= maxAboutPhotos;
                    const disabledGallery =
                      section === "gallery" &&
                      !inGallery &&
                      draftGalleryIds.length >= maxGalleryPhotos;

                    return (
                      <button
                        key={img.id}
                        type="button"
                        onClick={handlePick}
                        disabled={disabledAbout || disabledGallery}
                        className={cn(
                          "relative aspect-square overflow-hidden rounded-xl bg-muted text-left transition-colors",
                          ringActive
                            ? "border-4 border-premium-500 ring-[3px] ring-premium-500/40"
                            : "border-2 border-border hover:border-muted-foreground/40",
                          (disabledAbout || disabledGallery) && "opacity-40",
                        )}
                        aria-label={`${img.title}`}
                      >
                        <img
                          src={img.thumbUrl}
                          alt=""
                          className="h-full w-full object-cover"
                        />
                        <div className="pointer-events-none absolute inset-x-0 bottom-0 flex flex-wrap gap-0.5 bg-linear-to-t from-black/70 to-transparent p-1.5 pt-6">
                          {isHero ? (
                            <span className="rounded bg-white/90 px-1 py-0.5 text-[9px] font-bold uppercase text-foreground">
                              Banner
                            </span>
                          ) : null}
                          {inAbout ? (
                            <span className="rounded bg-white/90 px-1 py-0.5 text-[9px] font-bold uppercase text-foreground">
                              About
                            </span>
                          ) : null}
                          {inGallery ? (
                            <span className="rounded bg-white/90 px-1 py-0.5 text-[9px] font-bold uppercase text-foreground">
                              Site
                            </span>
                          ) : null}
                          {isProfile ? (
                            <span className="rounded bg-white/90 px-1 py-0.5 text-[9px] font-bold uppercase text-foreground">
                              You
                            </span>
                          ) : null}
                          {isLogo ? (
                            <span className="rounded bg-white/90 px-1 py-0.5 text-[9px] font-bold uppercase text-foreground">
                              Logo
                            </span>
                          ) : null}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
              {error ? (
                <p className="mt-4 text-sm text-destructive">{error}</p>
              ) : null}
            </div>
          </div>
        </div>

        <DialogFooter className="shrink-0 gap-2 border-t border-border px-5 py-4 sm:flex-row sm:justify-end">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={handleUploadFiles}
          />
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSave} disabled={saving || empty}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export function parsePhotoAssignmentFocus(
  raw: string | null | undefined,
): PhotoAssignmentSection {
  if (
    raw === "about" ||
    raw === "gallery" ||
    raw === "profile" ||
    raw === "logo"
  ) {
    return raw;
  }
  if (raw === "hero" || raw === "all" || raw == null || raw === "") {
    return "hero";
  }
  return "hero";
}
